/**
 * Models for the "Recover security code" (PIN reset) flow.
 *
 * Mirrors the mobile app flow: Settings → Recover security code →
 * verify password → choose OTP channel → enter OTP → set new PIN.
 *
 * Backend contract (gozem_money_auth):
 *  1. POST /api/gozem_money_auth/validate_password         → confirm password, get OTP channels
 *  2. POST /api/gozem_money_auth/generate_pin_recovery_otp → send the OTP via the chosen channel
 *  3. POST /api/gozem_money_auth/pin_recovery              → submit OTP + new (encrypted) PIN
 *
 * NOTE: there is no standalone "verify OTP" endpoint — the OTP is validated by
 * the backend as part of `pin_recovery`, alongside the new PIN. The password is
 * carried through all three calls (no intermediate token is issued).
 */

/** Channel used to deliver the OTP for PIN recovery. Returned dynamically by the backend. */
export type RecoverPinOtpChannel = 'email' | 'sms' | 'whatsapp';

/**
 * Steps of the recover-PIN flow, used to drive `gz-multi-step-flow`.
 *
 * Every screen is a step of the SAME dialog — the channel picker, the terminal
 * states and the discard confirmation included. GPGT-40873: the previous version
 * rendered those as stacked `gz-modal-shell`s, which is what made one Escape
 * close two layers and let Tab walk into the form hidden behind.
 *
 *  password → channel → otp → newPin → success
 *                                    ↘ blocked | failure | discard
 */
export type RecoverPinStep =
  | 'password'
  | 'channel'
  | 'otp'
  | 'newPin'
  | 'success'
  | 'blocked'
  | 'failure'
  | 'discard';

/**
 * Why the flow landed on the `failure` step.
 *
 * `network` is recoverable — the PIN was not changed, so the user can retry from
 * where they were. `session` is not: they have to sign in again.
 */
export type RecoverPinFailureKind = 'network' | 'session';

/** Response of `validate_password`. */
export interface ValidatePasswordResponse {
  success: boolean;
  is_password_valid: boolean;
  /** Channels the user may pick to receive the OTP (e.g. ["sms", "whatsapp"]). */
  otp_channels: RecoverPinOtpChannel[];
}

/** Response of `generate_pin_recovery_otp`. */
export interface GeneratePinRecoveryOtpResponse {
  success: boolean;
  /** Channel the OTP was actually sent through, e.g. "sms". */
  gateway: string;
  /** Human-readable expiry window, e.g. "30 minutes". */
  otp_expiry_delay: string;
}

/** Response of `pin_recovery`. */
export interface PinRecoveryResponse {
  success: boolean;
  message?: string;
}

/** Error body shared by the recover-pin endpoints (success === false). */
export interface RecoverPinErrorBody {
  success: false;
  error_code?: string | number;
  message?: string;
  metadata?: {
    remaining_attempts?: number;
    block_time?: number;
  };
}

/**
 * Typed error thrown by `RecoverPinApi`.
 *
 * Carries the backend `error_code` (e.g. "447" invalid password, "047" invalid OTP)
 * and lockout metadata so the store can route the flow and craft user-facing copy.
 */
export class RecoverPinError extends Error {
  constructor(
    readonly errorCode: string | null,
    message: string,
    readonly remainingAttempts: number | null = null,
    readonly blockTime: number | null = null,
  ) {
    super(message);
    this.name = 'RecoverPinError';
  }
}
